const TZ = "Z";

/**
 * Check if the supplied ISO-8601 datetime string contains a timezone.
 * We do this by checking if the end of the string matches the timezone formats.
 *
 * Valid timezone formats are `±hhmm`, `±hh:mm` or `Z`
 *
 * We don't use short `±hh` format because the Date constructor parses it as invalid
 * eg `new Date ('2021-03-05T11:45:00+01')` is invalid
 *
 * @param dateTime the datetime string.
 */
export const containsTimezone = (dateTime: string): boolean => {
  return /(Z|[+-]\d{2}:?\d{2})$/.test(dateTime);
};

/**
 * Add the DB server timezone to a ISO-8601 datetime string
 * @param dateTime
 */
const addTimezone = (dateTime: string): string => {
  return `${dateTime}${TZ}`;
};

/**
 * Get the ISO-8601 datetime always with timezone included
 * @param dateTime
 */
export const getDateTimeWithTimezone = (dateTime: string): string => {
  if (containsTimezone(dateTime)) {
    return dateTime;
  }
  return addTimezone(dateTime);
};

type ReturnValuesType =
  | "dd-mmm-yyyy"
  | "mmm dd, yyyy"
  | "yyyy-mm-dd"
  | "mmm dd, yyyy, hh:mm a"
  | "mmmm yyyy"
  | "hh:mm a";

const padZero = (num: number) => (num < 10 ? "0" + num : num);

export const formatDate = (
  date: string | Date,
  returnValues: ReturnValuesType = "mmm dd, yyyy",
) => {
  if (!date) return "";

  try {
    const _date =
      typeof date === "string" ? getDateTimeWithTimezone(date) : date;
    const d = new Date(_date);

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const day = d.getDate().toString().padStart(2, "0"); // Add leading zero if needed
    const numberedMonth = d.getMonth() + 1;
    const paddedMonth = padZero(numberedMonth);
    const month = months[d.getMonth()]; // Get month name from array
    const year = d.getFullYear();
    const hour = d.getHours();
    const formattedHour = hour < 10 ? `0${hour}` : hour;
    const minute = d.getMinutes();
    const formattedMinutes = minute < 10 ? `0${minute}` : minute;
    const amPm = hour < 12 ? "am" : "pm";

    if (returnValues === "dd-mmm-yyyy") return `${day}-${month}-${year}`;
    if (returnValues === "mmm dd, yyyy") return `${month} ${day}, ${year}`;
    if (returnValues === "yyyy-mm-dd") return `${year}-${paddedMonth}-${day}`;
    if (returnValues === "mmm dd, yyyy, hh:mm a")
      return `${month} ${day}, ${year}, ${formattedHour}:${formattedMinutes} ${amPm}`;
    if (returnValues === "mmmm yyyy") return `${month} ${year}`;
    if (returnValues === "hh:mm a")
      return `${formattedHour}:${formattedMinutes} ${amPm}`;

    return `${day}-${month}-${year}`;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return date.toString();
  }
};
